import { useLayoutEffect } from "react";
import { registerSuper } from "../analytics";

/**
 * What this tab IS, stamped on every event it sends from here on.
 *
 * The same bundle serves a television, a phone remote, the control panel and the admin.
 * Without this every breakdown has to be reconstructed from `$pathname`
 * — `/view/d/:id` means screen, `/remote` means remote, everything else means control —
 * which is a lookup table nobody remembers. One property, four values. ("landing" was a
 * fifth, and died with the marketing page: `/` mounts the screen surface now, so the
 * front door labels itself the way every other screen does.)
 *
 * A LAYOUT effect, deliberately: React runs every layout effect in the tree before any
 * passive effect, and the route's pageview is captured from a passive effect in
 * RouteAnalytics, a sibling ABOVE the route. Register here in a passive effect and the
 * first pageview of every visit goes out before the role is known.
 *
 * Re-registered on every mount of a role root, so a tab that changes role — a control tab
 * whose code gets claimed and becomes a screen — is relabelled the moment it does.
 */
type TabRole = "screen" | "control" | "remote" | "admin";

export function useAnalyticsRole(role: TabRole): void {
  useLayoutEffect(() => {
    registerSuper({ role });
  }, [role]);
}

/**
 * The same thing as an element, for a root that must stay hook-free.
 *
 * ControlApp is read as a plain function by tools/checks/home-surface.mts — `ControlApp()`
 * returns its element tree and the check walks it for the route table — which only works
 * while ControlApp calls no hooks of its own. Rendering this instead keeps that true.
 */
export function AnalyticsRole({ role }: { role: TabRole }): null {
  useAnalyticsRole(role);
  return null;
}
